/**
 * useOrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1Approval
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { emitCascadeEvent, useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, OrbDocTutorialsBeginnerCompleteOrbitalENExpense, OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalEventPayloadMap } from '@app/shared';
import { useUIEvents } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';

// ============================================================================
// State Machine
// ============================================================================

export type OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalState =
  | 'draft'
  | 'submitted'
  | 'approved'
  | 'rejected';

export type OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalEvent =
  | 'INIT'
  | 'RAISE'
  | 'SUBMIT'
  | 'APPROVE'
  | 'REJECT'
  | 'NEW_CLAIM'
  | 'REVISE'
  | 'MOVED';

type OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot = 'main';
interface OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalInternalState {
  machineState: OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalEvent | null;
  slotWriter: Partial<Record<OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot, OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocTutorialsBeginnerCompleteOrbitalENExpense>;
}

type OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalAction =
  | { type: 'EVENT_SUCCESS'; event: OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot[]; renderClear?: OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof OrbDocTutorialsBeginnerCompleteOrbitalENExpense & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function orbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalReducer(
  state: OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalInternalState,
  action: OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalAction
): OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<OrbDocTutorialsBeginnerCompleteOrbitalENExpense>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<OrbDocTutorialsBeginnerCompleteOrbitalENExpense>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<OrbDocTutorialsBeginnerCompleteOrbitalENExpense>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalInternalState = {
  machineState: 'draft',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
}

export interface OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalLogicReturn {
  state: OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalEvent | null;
  slotWriter: Partial<Record<OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot, OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocTutorialsBeginnerCompleteOrbitalENExpense>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalEventPayloadMap]: (payload?: OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalEventPayloadMap[K]) => Promise<EventResponse> };
  config?: OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalConfig;
}

export function useOrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalLogic(config?: Partial<OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalConfig>): OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalLogicReturn {
  const mergedConfig = config;

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/orb-doc-tutorials-beginner-complete-orbital-en-doc-ex1-approval', orbitalName: 'OrbDocTutorialsBeginnerCompleteOrbitalEN', traitName: 'OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1Approval' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(orbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, _payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const entity: EntityRow[] = liveData['OrbDocTutorialsBeginnerCompleteOrbitalENExpense'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};

    if (fromState === 'draft' && event === 'INIT') {
      // draft → draft (on INIT)
      _newState = 'draft';
    } else if (fromState === 'draft' && event === 'RAISE') {
      // draft → draft (on RAISE)
        _emits.push(((_p) => () => { eventBus.emit('MOVED', _p, { trait: 'OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1Approval' }); emitCascadeEvent(eventBus, 'OrbDocTutorialsBeginnerCompleteOrbitalEN', 'OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1Approval', { event: 'MOVED', payload: _p, source: { orbital: 'OrbDocTutorialsBeginnerCompleteOrbitalEN', trait: 'OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1Approval' } }); })({ event: "RAISE" as const }));
        _fields['amount'] = (+(fields?.amount ?? 0) + 50);
        fields = { ...(fields ?? {}), 'amount': _fields['amount'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'draft';
    } else if (fromState === 'draft' && event === 'SUBMIT' && ((+(fields?.amount ?? 0) > 0))) {
      // draft → submitted (on SUBMIT)
        _emits.push(((_p) => () => { eventBus.emit('MOVED', _p, { trait: 'OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1Approval' }); emitCascadeEvent(eventBus, 'OrbDocTutorialsBeginnerCompleteOrbitalEN', 'OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1Approval', { event: 'MOVED', payload: _p, source: { orbital: 'OrbDocTutorialsBeginnerCompleteOrbitalEN', trait: 'OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1Approval' } }); })({ event: "SUBMIT" as const }));
      _newState = 'submitted';
    } else if (fromState === 'submitted' && event === 'INIT') {
      // submitted → draft (on INIT)
      _newState = 'draft';
    } else if (fromState === 'submitted' && event === 'APPROVE' && ((+(fields?.amount ?? 0) <= 500))) {
      // submitted → approved (on APPROVE)
        _emits.push(((_p) => () => { eventBus.emit('MOVED', _p, { trait: 'OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1Approval' }); emitCascadeEvent(eventBus, 'OrbDocTutorialsBeginnerCompleteOrbitalEN', 'OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1Approval', { event: 'MOVED', payload: _p, source: { orbital: 'OrbDocTutorialsBeginnerCompleteOrbitalEN', trait: 'OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1Approval' } }); })({ event: "APPROVE" as const }));
        _fields['note'] = "Approved";
        fields = { ...(fields ?? {}), 'note': _fields['note'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'approved';
    } else if (fromState === 'submitted' && event === 'REJECT') {
      // submitted → rejected (on REJECT)
        _emits.push(((_p) => () => { eventBus.emit('MOVED', _p, { trait: 'OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1Approval' }); emitCascadeEvent(eventBus, 'OrbDocTutorialsBeginnerCompleteOrbitalEN', 'OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1Approval', { event: 'MOVED', payload: _p, source: { orbital: 'OrbDocTutorialsBeginnerCompleteOrbitalEN', trait: 'OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1Approval' } }); })({ event: "REJECT" as const }));
        _fields['note'] = "Rejected";
        fields = { ...(fields ?? {}), 'note': _fields['note'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'rejected';
    } else if (fromState === 'approved' && event === 'INIT') {
      // approved → draft (on INIT)
      _newState = 'draft';
    } else if (fromState === 'approved' && event === 'NEW_CLAIM') {
      // approved → draft (on NEW_CLAIM)
        _emits.push(((_p) => () => { eventBus.emit('MOVED', _p, { trait: 'OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1Approval' }); emitCascadeEvent(eventBus, 'OrbDocTutorialsBeginnerCompleteOrbitalEN', 'OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1Approval', { event: 'MOVED', payload: _p, source: { orbital: 'OrbDocTutorialsBeginnerCompleteOrbitalEN', trait: 'OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1Approval' } }); })({ event: "NEW_CLAIM" as const }));
        _fields['amount'] = 120;
        fields = { ...(fields ?? {}), 'amount': _fields['amount'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'draft';
    } else if (fromState === 'rejected' && event === 'INIT') {
      // rejected → draft (on INIT)
      _newState = 'draft';
    } else if (fromState === 'rejected' && event === 'REVISE') {
      // rejected → draft (on REVISE)
        _emits.push(((_p) => () => { eventBus.emit('MOVED', _p, { trait: 'OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1Approval' }); emitCascadeEvent(eventBus, 'OrbDocTutorialsBeginnerCompleteOrbitalEN', 'OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1Approval', { event: 'MOVED', payload: _p, source: { orbital: 'OrbDocTutorialsBeginnerCompleteOrbitalEN', trait: 'OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1Approval' } }); })({ event: "REVISE" as const }));
      _newState = 'draft';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot[]; clear: OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot[] } => {
    switch (fromState) {
      case 'approved':
        switch (event) {
          case 'INIT': return { write: ['main'] as OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot[], clear: [] as OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot[] };
          case 'NEW_CLAIM': return { write: ['main'] as OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot[], clear: [] as OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'draft':
        switch (event) {
          case 'INIT': return { write: ['main'] as OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot[], clear: [] as OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot[] };
          case 'SUBMIT': return { write: ['main'] as OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot[], clear: [] as OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'rejected':
        switch (event) {
          case 'INIT': return { write: ['main'] as OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot[], clear: [] as OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot[] };
          case 'REVISE': return { write: ['main'] as OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot[], clear: [] as OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'submitted':
        switch (event) {
          case 'APPROVE': return { write: ['main'] as OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot[], clear: [] as OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot[] };
          case 'INIT': return { write: ['main'] as OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot[], clear: [] as OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot[] };
          case 'REJECT': return { write: ['main'] as OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot[], clear: [] as OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'OrbDocTutorialsBeginnerCompleteOrbitalENExpense': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    void sendEvent; void commandPump; // no server-touching effect on this trait; shared-store commit above is the only sync needed
    return { success: true, transitioned: true, states: { 'OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1Approval': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalEventPayloadMap]: (payload?: OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'RAISE': () => dispatch('RAISE'),
    'SUBMIT': () => dispatch('SUBMIT'),
    'APPROVE': () => dispatch('APPROVE'),
    'REJECT': () => dispatch('REJECT'),
    'NEW_CLAIM': () => dispatch('NEW_CLAIM'),
    'REVISE': () => dispatch('REVISE'),
    'MOVED': (payload?: OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1ApprovalEventPayloadMap['MOVED']) => dispatch('MOVED', payload),
  };

  useUIEvents(enqueueEvent, 'OrbDocTutorialsBeginnerCompleteOrbitalEN.OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1Approval', ['INIT', 'RAISE', 'SUBMIT', 'APPROVE', 'REJECT', 'NEW_CLAIM', 'REVISE'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1Approval', (): TraitStateSnapshot => ({
      traitName: 'OrbDocTutorialsBeginnerCompleteOrbitalENDocEx1Approval',
      currentState: internalStateRef.current.machineState,
      states: ['draft', 'submitted', 'approved', 'rejected'],
      events: ['INIT', 'RAISE', 'SUBMIT', 'APPROVE', 'REJECT', 'NEW_CLAIM', 'REVISE', 'MOVED'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: [],
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
